import {
  OrgTestEmailType,
  useOrganizationSendTestEmailsMutation,
  useOrganizationStaffQuery,
  UserWithPictureTc,
} from "gql/generated";
import { Mail } from "iconoir-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components";
import { Flex } from "styled-system/jsx";
import { Select, Text, useToastContext } from "@ttc3k/trekker";

type SendTestEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  emailType: "claim" | "ping";
};

const EMAIL_TYPE_MAP: Record<"claim" | "ping", OrgTestEmailType> = {
  claim: OrgTestEmailType.OperatorInvite,
  ping: OrgTestEmailType.PingAgain,
};

export const SendTestEmailModal = ({
  isOpen,
  onClose,
  organizationId,
  emailType,
}: SendTestEmailModalProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.DIRECTORY_SETTINGS.CUSTOM_EMAIL.TEST_MODAL",
  });

  const { toastFactory } = useToastContext();
  const [recipientUserId, setRecipientUserId] = useState("");

  const { data: staffData } = useOrganizationStaffQuery({
    variables: { organizationId },
    skip: !isOpen || !organizationId,
  });

  const collaborators = (staffData?.organizationStaff ?? []) as UserWithPictureTc[];

  const [sendTestEmails, { loading }] = useOrganizationSendTestEmailsMutation();

  useEffect(() => {
    if (!isOpen) setRecipientUserId("");
  }, [isOpen]);

  const recipientOptions = useMemo(
    () =>
      collaborators
        .filter(user => user.email)
        .map(user => ({
          label: `${user.firstName} ${user.lastName} (${user.email})`,
          value: user._id,
        })),
    [collaborators],
  );

  const recipientCollection = useMemo(
    () => Select.createCollection({ items: recipientOptions }),
    [recipientOptions],
  );

  const onSend = useCallback(async () => {
    if (!recipientUserId) return;

    try {
      const result = await sendTestEmails({
        variables: {
          organizationId,
          recipientUserId,
          emailTypes: [EMAIL_TYPE_MAP[emailType]],
        },
      });

      const payload = result.data?.organizationSendTestEmails;
      const sentCount = payload?.sentCount ?? 0;

      if (sentCount > 0) {
        toastFactory.create({
          title: t("SUCCESS_TITLE"),
          description: t("SUCCESS_DESCRIPTION"),
        });
        onClose();
        return;
      }

      const firstError = payload?.failed?.[0]?.error;
      toastFactory.create({
        title: t("ERROR_TITLE"),
        description: firstError ?? t("ERROR_DESCRIPTION"),
      });
    } catch (err) {
      toastFactory.create({
        title: t("ERROR_TITLE"),
        description:
          err instanceof Error ? err.message : t("ERROR_DESCRIPTION"),
      });
    }
  }, [emailType, onClose, organizationId, recipientUserId, sendTestEmails, t, toastFactory]);

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("TITLE")}
      description={t("DESCRIPTION")}
      HeaderIcon={Mail}
      size="md"
      saveButtonCopy={t("SEND")}
      cancelButtonCopy={t("CANCEL")}
      saveButtonProps={{
        visual: "accent.blue",
        disabled: !recipientUserId || !organizationId,
        isLoading: loading,
        onClick: onSend,
      }}
      cancelButtonProps={{ onClick: onClose }}
      isOverflowVisible
    >
      <Flex flexDir="column" gap="150">
        <Text visual="bodySemiBold" color="text.dark">
          {t("RECIPIENT_LABEL")}
        </Text>
        {recipientOptions.length === 0 ? (
          <Text visual="smallRegular" color="text.light">
            {t("NO_RECIPIENTS")}
          </Text>
        ) : (
          <Select.Root
            collection={recipientCollection}
            value={recipientUserId ? [recipientUserId] : []}
            onValueChange={({ value }: { value: string[] }) =>
              setRecipientUserId(value[0] ?? "")
            }
          >
            {recipientCollection.items.map(option => (
              <Select.Item key={option.value} item={option} />
            ))}
          </Select.Root>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
